import { useEffect, useId, useRef, useState } from 'react'
import { apiErrorMessage } from './tracking/trackingUtils'

const REASONS = ['Ordered by mistake', 'Found a better price', 'Delivery is taking too long', 'Want to change the item or address']

/**
 * "Cancel order" - only offered before the order is handed to Shiprocket (the
 * API's can_cancel). A paid order is refunded to the original payment through
 * Razorpay automatically. `onSubmit(reason)` returns the API's promise; the
 * dialog closes on success and shows plain copy on failure.
 */
export default function CancelOrderModal({ open, onClose, onSubmit, paid }) {
  const titleId = useId()
  const dialogRef = useRef(null)
  const [choice, setChoice] = useState('')
  const [other, setOther] = useState('')
  const [error, setError] = useState('')
  const [failure, setFailure] = useState('')
  const [saving, setSaving] = useState(false)
  // Read through a ref so a new onClose each render doesn't re-run the effect (and refocus).
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    if (!open) return undefined
    const opener = document.activeElement
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    dialogRef.current?.querySelector('input, textarea, button')?.focus()
    const onKey = (e) => {
      if (e.key === 'Escape') closeRef.current()
      // Keep Tab inside the dialog.
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = [...dialogRef.current.querySelectorAll('button, input, textarea, [tabindex]:not([tabindex="-1"])')].filter((el) => !el.disabled)
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last?.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first?.focus()
        }
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
      opener?.focus?.()
    }
  }, [open])

  if (!open) return null

  const submit = async (e) => {
    e.preventDefault()
    const reason = choice === 'other' ? other.trim() : choice
    if (reason.length < 3) {
      setError(choice === 'other' ? 'Tell us a little more (at least 3 characters).' : 'Pick a reason.')
      return
    }
    setError('')
    setSaving(true)
    setFailure('')
    try {
      await onSubmit(reason)
      onClose()
    } catch (err) {
      setFailure(apiErrorMessage(err, "We couldn't cancel this order. Please try again or contact support."))
    } finally {
      setSaving(false)
    }
  }

  const radio = 'flex items-center gap-3 px-4 h-12 rounded-xl border text-sm text-primary cursor-pointer transition-colors'

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-primary/40" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-white rounded-3xl shadow-xl p-6 sm:p-8 normal-case"
      >
        <div className="flex items-start justify-between gap-4 mb-2">
          <h2 id={titleId} className="text-lg font-heading text-primary tracking-wider uppercase">
            Cancel order
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" className="w-9 h-9 -mr-2 -mt-1 rounded-full text-primary/50 hover:text-primary hover:bg-primary/5">
            <i className="fa-solid fa-xmark" aria-hidden="true"></i>
          </button>
        </div>
        <p className="text-sm text-primary/60 mb-6">
          {paid
            ? 'The full amount is refunded to your original payment method straight away. Banks usually take 5–7 working days to show it.'
            : "Nothing has been charged, so there's nothing to refund."}
        </p>

        <form onSubmit={submit} noValidate className="space-y-3">
          <fieldset className="space-y-2">
            <legend className="block text-xs text-primary/60 tracking-wider mb-1.5">Why are you cancelling?</legend>
            {[...REASONS, 'other'].map((r) => (
              <label key={r} className={`${radio} ${choice === r ? 'border-secondary bg-secondary/5' : 'border-border hover:border-secondary/50'}`}>
                <input type="radio" name="cancel-reason" value={r} checked={choice === r} onChange={() => setChoice(r)} className="accent-secondary" />
                {r === 'other' ? 'Something else' : r}
              </label>
            ))}
          </fieldset>
          {choice === 'other' && (
            <textarea
              rows={3}
              value={other}
              onChange={(e) => setOther(e.target.value)}
              maxLength={500}
              aria-label="Your reason"
              className="w-full px-4 py-3 rounded-xl border border-border bg-white text-sm text-primary focus:outline-none focus:border-secondary"
              placeholder="Tell us what went wrong"
            />
          )}
          {error && <p className="text-xs text-red-600">{error}</p>}

          {failure && (
            <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              {failure}
            </p>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-3">
            <button type="button" onClick={onClose} className="h-12 px-6 rounded-xl border border-primary/20 text-primary text-xs font-bold tracking-[0.15em] uppercase hover:border-secondary hover:text-secondary">
              Keep order
            </button>
            <button type="submit" disabled={saving} className="h-12 px-6 grow rounded-xl bg-red-600 text-white text-xs font-bold tracking-[0.15em] uppercase hover:bg-red-700 disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {saving && <i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>}
              {saving ? 'Cancelling…' : 'Cancel order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
